import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import dayjs from 'dayjs';
import '../css/KenterEnergyWidget.css';
import api from './api';

/**
 * Widget that shows energy usage and efficiency from Kenter API
 * comparing previous energy year to current with prediction
 */
const KenterEnergyWidget = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [usageData, setUsageData] = useState({});
  const [chartData, setChartData] = useState([]);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [lastMonth, setLastMonth] = useState(null); // Last month with data
  const [efficiencyData, setEfficiencyData] = useState(null);
  
  // Get current energy year for reference
  const currentDate = new Date();
  const currentMonthNum = currentDate.getMonth() + 1; // JS months are 0-indexed
  const currentYear = currentDate.getFullYear();
  const currentEnergyYear = currentMonthNum >= 7 ? 
    `${currentYear}-${currentYear+1}` : 
    `${currentYear-1}-${currentYear}`;
  const previousEnergyYear = `${parseInt(currentEnergyYear.split('-')[0]) - 1}-${parseInt(currentEnergyYear.split('-')[1]) - 1}`;
  
  useEffect(() => {
    // Fetch energy usage data from the backend
    const fetchUsageData = async () => {
      setLoading(true);
      setError(null);
      
      try {
        // Fetch last 3 energy years (current + 2 previous)
        const currentEnergyYearStart = currentMonthNum >= 7 ? currentYear : currentYear - 1;
        const startYear = currentEnergyYearStart - 2;
        
        const response = await api.get('/api/kenter/usage', {
          params: {
            startYear,
            endYear: currentEnergyYearStart
          }
        });
        
        // Fetch last update time and determine last month with data
        const lastUpdateRes = await api.get('/api/kenter/last-update', {
          params: {
            // Only consider data from current energy year
            minYear: currentEnergyYearStart
          }
        });
        let lastDataMonth = null;
        let lastDate = null;
        let efficiencyRes = null;
        
        if (lastUpdateRes.data && lastUpdateRes.data.lastUpdate) {
          setLastUpdate(dayjs(lastUpdateRes.data.lastUpdate).format('DD-MM-YYYY HH:mm'));
          
          // IMPORTANT: For Kenter, data with date 2025-04-01 is for March 2025
          // We need to get the month BEFORE the date timestamp
          if (lastUpdateRes.data.lastDate) {
            lastDate = dayjs(lastUpdateRes.data.lastDate); // Assign the value here
            
            // Get month before the reported date (this is the actual usage month)
            const usageMonth = lastDate.subtract(0, 'month');
            lastDataMonth = usageMonth;

            // Fetch efficiency data with the correct date range
            efficiencyRes = await api.get('/api/degree-days/efficiency', {
              params: {
                endDate: lastDate.subtract(1, 'day').format('YYYY-MM-DD') // Since Kenter data with date X is for month X-1
              }
            });

            // Format month name in Dutch
            const monthNames = [
              'januari', 'februari', 'maart', 'april', 'mei', 'juni',
              'juli', 'augustus', 'september', 'oktober', 'november', 'december'
            ];
            
            setLastMonth(`t/m ${monthNames[usageMonth.month()]} ${usageMonth.year()}`);
          } else {
            // If no lastDate, fetch efficiency without date range
            efficiencyRes = await api.get('/api/degree-days/efficiency');
          }
        } else {
          // If no lastUpdate data, fetch efficiency without date range
          efficiencyRes = await api.get('/api/degree-days/efficiency');
        }

        // Set efficiency data from the response
        if (efficiencyRes) {
          setEfficiencyData(efficiencyRes.data);
        }
        
        setUsageData(response.data);
        
        // Process data for chart - pass last month information to use for prediction
        processChartData(response.data, lastDataMonth);
      } catch (err) {
        console.error('❌ Error fetching Kenter energy data:', err);
        setError('Kon de energiegegevens niet laden. Probeer het later nog eens.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchUsageData();
  }, [currentMonthNum, currentYear]);
  
  // Process API data into format for chart
  const processChartData = (data, lastMonthData) => {
    // Ensure we have data for current and previous energy years
    if (!data || !data[currentEnergyYear] || !data[previousEnergyYear]) {
      setChartData([]);
      return;
    }
    
    // Previous year total
    const previousYearTotal = data[previousEnergyYear].total;
    
    // Current year to date
    const currentYearToDate = data[currentEnergyYear].total;
    
    // Prediction for current year based on lastMonthData
    let prediction = 0;
    
    if (lastMonthData) {
      // Define seasonality map
      const seasonalityMap = {
        '07': 0.01, '08': 0.03, '09': 0.05, '10': 0.12,
        '11': 0.24, '12': 0.40, '01': 0.57, '02': 0.71,
        '03': 0.84, '04': 0.93, '05': 0.98, '06': 1.00
      };
      
      // Get the month number as string (01-12)
      const monthStr = (lastMonthData.month() + 1).toString().padStart(2, '0');
      const seasonFraction = seasonalityMap[monthStr] || 0.5;
      
      // Calculate prediction based on the last available month's data
      console.log(`Using season fraction ${seasonFraction} for month ${monthStr}`);
      prediction = seasonFraction > 0 ? 
        Math.round(currentYearToDate / seasonFraction) : currentYearToDate;
    } else if (data[currentEnergyYear].prediction) {
      // Fallback to API prediction if available
      prediction = data[currentEnergyYear].prediction;
    }
    
    // Format for chart
    const formattedData = [
      {
        name: 'Gebouw Totaal',
        vorigJaar: previousYearTotal,
        huidigJaar: currentYearToDate,
        voorspelling: prediction
      }
    ];
    
    setChartData(formattedData);
  };
  
  // Format numbers with thousands separator
  const formatNumber = (num) => {
    if (num === undefined || num === null) return 'N/A';
    return new Intl.NumberFormat('nl-NL').format(Math.round(num));
  };
  
  // Calculate percentage change between values
  const calculatePercentChange = (current, previous) => {
    if (!previous || previous === 0) return '∞';
    const change = ((current - previous) / previous) * 100;
    return change.toFixed(1);
  };
  
  // Calculate efficiency for display
  const calculateEfficiency = (usage, degreeDays) => {
    if (!degreeDays || degreeDays === 0) return 'N/A';
    return (usage / degreeDays).toFixed(2);
  };

  // Handle manual sync with Kenter API and Degree Days
  const handleSync = async () => {
    if (loading) return;
    
    setLoading(true);
    setError(null);
    
    try {
      // First trigger sync with Kenter API
      await api.get('/api/kenter/sync');
      
      // Then sync degree days for the last 6 months to the day before yesterday
      const sixMonthsAgo = dayjs().subtract(6, 'month').format('YYYY-MM-DD');
      const twoDaysAgo = dayjs().subtract(2, 'day').format('YYYY-MM-DD');
      
      await api.get('/api/degree-days/sync-archive', {
        params: {
          start: sixMonthsAgo,
          end: twoDaysAgo
        }
      });
      
      // Refresh data after sync
      window.location.reload();
    } catch (err) {
      console.error('❌ Error syncing data:', err);
      setError('Synchronisatie mislukt. Probeer het later nog eens.');
      setLoading(false);
    }
  };
  
  if (loading) {
    return (
      <div className="kenter-energy-widget card">
        <h3>🔋 Gebouw Energieverbruik</h3>
        <div className="tiny-loading-indicator">
        <div className="micro-spinner"></div>
          <p>Bezig met syncen Kenter en Graaddagen...</p>
        </div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="kenter-energy-widget card">
        <h3>🔋 Gebouw Energieverbruik</h3>
        <div className="error-message">
          <p>{error}</p>
          <button className="sync-button" onClick={handleSync}>
            Synchroniseren met Kenter
          </button>
        </div>
      </div>
    );
  }
  
  // Extract data for current and previous energy years
  const currentYearData = usageData[currentEnergyYear] || {};
  const previousYearData = usageData[previousEnergyYear] || {};
  
  // Get values for display
  const currentTotal = currentYearData.total || 0;
  const previousTotal = previousYearData.total || 0;
  const prediction = chartData.length > 0 ? chartData[0].voorspelling : 0;
  
  // Get percent change between prediction and previous year
  const percentChange = calculatePercentChange(prediction, previousTotal);
  const percentChangeClass = parseFloat(percentChange) < 0 ? 'negative-change' : 'positive-change';
  
  // Get efficiency values
  const currentEfficiency = efficiencyData?.currentYear?.efficiency || 0;
  const previousEfficiency = efficiencyData?.previousYear?.efficiency || 0;
  const efficiencyChange = efficiencyData?.percentChange || 0;
  
  // Get degree days values
  const currentDegreeDays = efficiencyData?.currentYear?.totalDegreeDays || 0;
  const previousDegreeDays = efficiencyData?.previousYear?.totalDegreeDays || 0;
  
  // Calculate efficiencies
  const prevYearEfficiency = calculateEfficiency(previousTotal, previousDegreeDays);
  const currentYearEfficiency = calculateEfficiency(currentTotal, currentDegreeDays);
  
  return (
    <div className="kenter-energy-widget">
      <h3>
        🔋 Gebouw Energieverbruik
      </h3>
      
      {lastUpdate && (
        <div className="last-update">
          Laatste update: {lastUpdate}
          <button className="sync-button small" onClick={handleSync} disabled={loading}>
            {loading ? 'Bezig...' : '🔄'}
          </button>
        </div>
      )}
      
      <div className="chart-container1">
        <ResponsiveContainer width="100%" height={200}>
          <BarChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="name" tick={false} />
            <YAxis />
            {/* Removing the Tooltip component entirely to disable hover tooltips */}
            <Legend />
            <Bar dataKey="vorigJaar" name={`Vorig jaar (${previousEnergyYear})`} fill="#3B82F6" />
            <Bar dataKey="huidigJaar" name={`Dit jaar tot nu`} fill="#10B981" />
            <Bar dataKey="voorspelling" name="Voorspelling totaal" fill="#FBBF24" />
          </BarChart>
        </ResponsiveContainer>
      </div>
      
      <div className="energy-metrics">
        <div className="metric-card previous-year">
          <div className="metric-label">Vorig jaar<br />({previousEnergyYear})</div>
          <div className="metric-value">{formatNumber(previousTotal)} m³</div>
          {previousDegreeDays > 0 && (
            <div className="metric-subtext">
              {formatNumber(previousDegreeDays)} graaddagen
            </div>
          )}
          {prevYearEfficiency !== 'N/A' && (
            <div className="efficiency-indicator">
              {prevYearEfficiency} m³/value
            </div>
          )}
        </div>
        
        <div className="metric-card current-year">
          <div className="metric-label">Dit jaar tot nu</div>
          <div className="metric-value">{formatNumber(currentTotal)} m³</div>
          {currentDegreeDays > 0 && (
            <div className="metric-subtext">
              {formatNumber(currentDegreeDays)} graaddagen
            </div>
          )}
          {currentYearEfficiency !== 'N/A' && (
            <div className="efficiency-indicator">
              {currentYearEfficiency} m³/value
            </div>
          )}
        </div>
       
        <div className="metric-card prediction">
          <div className="metric-label">Voorspelling totaal<br />({currentEnergyYear})</div>
          <div className="metric-value">{formatNumber(prediction)} m³</div>
          <div className={`percent-badge ${percentChangeClass}`}>{percentChange}%</div>
        </div>
      </div>
      {lastMonth && (
        <div className="month-label">
          {lastMonth}
        </div>
      )}
    
      <div className="efficiency-metrics">
        <div className="metric-card efficiency">
          <div className="metric-label">
            Efficiëntie<br />(m³/graaddag)
            <div className="tooltip-icon" aria-label="Meer informatie" tabIndex="0">
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="12" y1="16" x2="12" y2="12"></line>
                <line x1="12" y1="8" x2="12.01" y2="8"></line>
              </svg>
              <div className="tooltip-content">
                <h3>Efficiëntie (m³/graaddag)</h3>
                <p>Deze waarde toont hoe efficiënt het gebouw energie verbruikt in verhouding tot het aantal graaddagen.</p>
                <p>Een <strong>lagere waarde</strong> betekent een betere efficiëntie - het gebouw verbruikt minder gas per graaddag.</p>
                <p>De <strong>{efficiencyChange}%</strong> wijst op een {efficiencyChange < 0 ? 'verbetering' : 'verslechtering'} van {Math.abs(efficiencyChange)}% in efficiëntie ten opzichte van vorig jaar.</p>
                <p>Graaddagen zijn een maat voor de verwarmingsbehoefte gebaseerd op buitentemperatuur.</p>
                <div className="tooltip-footer">Bron: Kenter & Open Meteo</div>
              </div>
            </div>
          </div>
          <div className="metric-value">{currentEfficiency.toFixed(2)}</div>
          {efficiencyChange !== 0 && (
            <div className={`percent-badge ${efficiencyChange < 0 ? 'negative-change' : 'positive-change'}`}>
              {efficiencyChange > 0 ? '+' : ''}{efficiencyChange.toFixed(1)}%
            </div>
          )}
        </div>
      </div>
      
      <div className="widget-footer">
        <span>Bron: Kenter & Open Meteo</span>
        <Link 
          to="/kenter-admin" 
          style={{
            fontSize: '12px',
            marginLeft: '10px',
            backgroundColor: '#246834',
            color: 'white',
            padding: '2px 8px',
            borderRadius: '4px',
            textDecoration: 'none',
            verticalAlign: 'middle'
          }}
        >
          admin
        </Link>
      </div>
    </div>
  );
};

export default KenterEnergyWidget;